import { Component, DetailEvent, Entity, registerComponent, registerSystem, System } from "aframe";
import { Camera, Object3D, PerspectiveCamera, PositionalAudio, Vector2, Vector3 } from "three";
import { InputActionDetail } from "./input/common";
import { MouseInputSystem } from "./input/mouse";
import { LayersSystem } from "./layers";

/*

"director" component : handles an actor-centric camera

*/

interface DirectorSystem extends System
{
	director : DirectorComponent;
	vrOrigin : Object3D;
	leftEyeLayers : number;
	rightEyeLayers : number;
	defaultLayers : number;

	data : 
	{
		leftEyeLayers : string[];
		rightEyeLayers : string[];
		defaultLayers : string[];
	}
}


interface DirectorComponent extends Component
{
	system : DirectorSystem;
	
	data : 
	{
		actor : Entity;
		near : number;
		far : number;
		horizontalSensivity : number;
		verticalSensivity : number;
		horizontalOffset : number;
		verticalOffset : number;
		fixed : boolean;
	},
	
	
	inputs : InputActionDetail[];
	mousePos? : Vector2;
	onMouseMove(mouseX : number, mouseY : number) : void;
	rotate(rotX : number, rotY : number) : void;
	followActor(zoom : number) : void;
}


const clamp = (v:number, min:number, max:number) => (v < min) ? min : ((v > max) ? max : v);



registerSystem("director", {

	schema : {
		leftEyeLayers : { type : "array", default: ["Default"] },
		rightEyeLayers : { type : "array", default: ["Default"] },
		defaultLayers : { type : "array", default: ["Default"] }
	},
	
	init(this : DirectorSystem)
	{
		const putThisAfter = (name : string) =>
		{
			const names = this.el.systemNames;
			names.splice(names.indexOf("director"), 1);
			names.splice(names.indexOf(name) + 1, 0, "director");
		};

 		this.el.addEventListener("enter-vr", () => putThisAfter("camera"));
		this.el.addEventListener("exit-vr", () => putThisAfter("avatar"));

		this.vrOrigin = new Object3D();
		this.el.object3D.add(this.vrOrigin);
	},

	update(this : DirectorSystem)
	{
		const layersSystem = this.el.systems.layers as LayersSystem;

		this.leftEyeLayers = layersSystem.maskOf( this.data.leftEyeLayers );
		this.rightEyeLayers = layersSystem.maskOf( this.data.rightEyeLayers );
		this.defaultLayers = layersSystem.maskOf( this.data.defaultLayers );
	},
	
	tick(this:DirectorSystem, t:number, dt:number)
	{
		const instance = this.director;
		
		const data = instance && instance.data;
		if(!(data && data.actor)) return;
		const sceneEl = this.el, obj = instance.el.object3D;

		const xr = sceneEl.renderer.xr;
		if(xr && xr.enabled && xr.isPresenting)
		{
			const xrCam = xr.getCamera();
			sceneEl.camera.layers.mask = xrCam.layers.mask = this.leftEyeLayers | this.rightEyeLayers;
			xrCam.cameras[0].layers.mask = this.leftEyeLayers;
			xrCam.cameras[1].layers.mask = this.rightEyeLayers;

			if( (obj.parent !== this.vrOrigin) ) //First VR frame !!!
			{
				const camMat = xrCam.matrixWorld, actorMat = data.actor.object3D.matrixWorld;
				const srcPx = camMat.elements[12], srcPz = camMat.elements[14], 
					srcKx = camMat.elements[8], srcKz = camMat.elements[10];
				
				const dstPx = actorMat.elements[12], dstPy = actorMat.elements[13], dstPz = actorMat.elements[14], 
					dstIx = -actorMat.elements[0], dstIz = -actorMat.elements[2],
					dstKx = -actorMat.elements[8], dstKz = -actorMat.elements[10];

				const Q = -1.0 / (srcKx * srcKx + srcKz * srcKz);
				const u = (srcPx * srcKz - srcPz * srcKx) * Q;
				const w = (srcPz * srcKz + srcPx * srcKx) * Q;
				this.vrOrigin.position.set( dstPx + dstIx * u + dstKx * w, dstPy, dstPz + dstIz * u + dstKz * w );
				this.vrOrigin.rotation.y = Math.atan2( dstKx, dstKz ) - Math.atan2( srcKx, srcKz );
				this.vrOrigin.add(obj);
				this.vrOrigin.updateWorldMatrix(true, true);
			}
		}
		else
		{
			if( obj.parent === this.vrOrigin ) // First non-VR frame
			{
				(instance.el.parentEl || sceneEl).object3D.add(obj);
				obj.matrixWorld.decompose(obj.position, obj.quaternion, obj.scale);
				obj.updateWorldMatrix(true, true);
			}

			if( data.far > 0 ) //3rd person
				sceneEl.camera.layers.mask = this.defaultLayers;
			else
				sceneEl.camera.layers.mask = this.leftEyeLayers | this.rightEyeLayers;

			let rotX = 0, rotY = 0, zoom = 0.0;
			for(let index = instance.inputs.length; --index >= 0; )
			{
				const input = instance.inputs[index];
				if(input.action == "CameraLeft") rotY += input.amount;
				else if(input.action == "CameraRight") rotY -= input.amount;
				else if(input.action == "CameraUp") rotX += input.amount;
				else if(input.action == "CameraDown") rotX -= input.amount;
				else if(input.action == "CameraZoomIn") zoom += input.amount;
				else if(input.action == "CameraZoomOut") zoom -= input.amount;
				
				if(input.instant)
					instance.inputs.splice(index, 1);
			}

			if(rotX || rotY)
				instance.rotate( data.verticalSensivity * rotX * dt, data.horizontalSensivity * rotY * dt );

			instance.followActor(zoom);
		}

		obj.updateWorldMatrix(true, false);
	}
});




registerComponent("director", {
	schema : {
		actor : { type : "selector" },
		fixed : { type : "boolean", default : true },
		near : {type : "number", default : 1 },
		far : {type : "number", default : 3 },
		horizontalSensivity : { type : "number", default : -0.003 },
		verticalSensivity : { type : "number", default : 0.003 },
		horizontalOffset : { type : "number", default : 0.5 },
		verticalOffset : { type : "number", default : 1.7 }
	},
	init(this : DirectorComponent)
	{
		
		this.inputs = [];
		const sceneEl = this.el.sceneEl!;
		const mouseSystem = sceneEl.systems.mouse as MouseInputSystem;

		this.system.director = this;
		this.el.object3D.rotation.order = 'YXZ';


		const mouseMove = (event : PointerEvent) => { 
			//console.log(event.clientX, event.clientY);
			this.mousePos && mouseSystem.mouseClientPosition &&  
			this.onMouseMove(mouseSystem.mouseClientPosition.x, mouseSystem.mouseClientPosition.y); 
		}

		const inputBegin = ((beginEvent : DetailEvent<InputActionDetail>) =>
		{
			if(beginEvent.detail.action == "MouseCamera")
			{
				if(!mouseSystem.mouseClientPosition)
					return; //Oops.

				this.mousePos = new Vector2(mouseSystem.mouseClientPosition.x, mouseSystem.mouseClientPosition.y);

				const stop = () =>
				{
					sceneEl.removeEventListener("pointermove", mouseMove);
					sceneEl.removeEventListener("input-end", inputEnd);
					sceneEl.removeEventListener("enter-ui", stop);
					delete this.mousePos;
					this.el.emit("exit-mousecamera");
				};
				const inputEnd = ((endEvent : DetailEvent<InputActionDetail>) =>
				{
					if(endEvent.detail === beginEvent.detail) stop();
				}) as EventListener;

				sceneEl.addEventListener("pointermove", mouseMove as EventListener);
				sceneEl.addEventListener("input-end", inputEnd);
				sceneEl.addEventListener("enter-ui", stop, { once : true });
				this.el.emit("enter-mousecamera");
			}
			else if(beginEvent.detail.action.startsWith("Camera"))
			{
				this.inputs.push(beginEvent.detail);
			}
		}) as EventListener;

		
		const inputEnd = ((event : DetailEvent<InputActionDetail>) =>
		{
			const ix = this.inputs.indexOf(event.detail);
			if(ix >= 0)
				this.inputs.splice(ix, 1);
		}) as EventListener;

		
		if(!sceneEl.is("vr-mode"))
			sceneEl.addEventListener("input-begin", inputBegin);

		sceneEl.addEventListener("enter-vr", () => sceneEl.removeEventListener("input-begin", inputBegin));
		sceneEl.addEventListener("exit-vr", () => sceneEl.addEventListener("input-begin", inputBegin));

		sceneEl.addEventListener("input-end", inputEnd); //Always
	},
	
	onMouseMove(this:DirectorComponent, mouseX : number, mouseY : number )
	{
		const mouse = this.mousePos!, data = this.data
		const drX = (mouseY - mouse.y) * data.verticalSensivity, drY = (mouseX - mouse.x) * data.horizontalSensivity;
		mouse.set(mouseX, mouseY);
		this.rotate(drX, drY);
	},

	rotate(this:DirectorComponent, dX : number, dY : number )
	{
		const actor = this.data.actor.object3D;
		actor.updateWorldMatrix(true, false);
		const acMat = actor.matrixWorld.elements;
		
		const obj = this.el.object3D;
		obj.updateWorldMatrix(true, false);

		const offY = this.data.verticalOffset;
		const pos = obj.position, rot = obj.rotation, mat = obj.matrixWorld.elements;

		const rX = clamp(rot.x + dX, -0.35 * Math.PI, 0.35 * Math.PI );	
		dX = rX - rot.x;

		const cosdX = Math.cos(dX), sindX = Math.sin(dX), cosdY = Math.cos(dY), sindY = Math.sin(dY);
		
		const dirX = mat[12] - acMat[12], dirY = mat[13] - (acMat[13] + offY), dirZ = mat[14] - acMat[14];
		const h = Math.sqrt(dirX * dirX + dirZ * dirZ );
		if(h > 0)
		{
			const qXZ = cosdX + dirY * sindX / h;
			pos.set(
				acMat[12] + (dirX * cosdY + dirZ * sindY) * qXZ,
				acMat[13] + offY + dirY * cosdX - h * sindX,
				acMat[14] + (dirZ * cosdY - dirX * sindY) * qXZ
			);
		}
		rot.set(rX, rot.y + dY, 0, rot.order);
	},

	followActor(this:DirectorComponent, zoom : number )
	{
		this.data.actor.object3D.updateWorldMatrix(true, false);
		this.el.object3D.updateWorldMatrix(true, false);

		const actorMat = this.data.actor.object3D.matrixWorld.elements;
		const thisMat = this.el.object3D.matrixWorld.elements;
		const offX = this.data.horizontalOffset, offY = this.data.verticalOffset;

		//Point of interest = actor position + offset.y
		const poi = { x : actorMat[12], y : actorMat[13] + offY, z : actorMat[14] };

		
		const doi = { x : thisMat[12] - poi.x, y : thisMat[13] - poi.y, z : thisMat[14] - poi.z };
		if(zoom != 0)
		{
			const kZoom = Math.pow(0.95, zoom);
			doi.x *= kZoom;
			doi.y *= kZoom;
			doi.z *= kZoom;
		}

		if( !this.data.fixed ) // LoZ:OoT camera
		{
			/* Camera/poi distance should be between near and far */

			const hSq = doi.x * doi.x + doi.z * doi.z, vSq = doi.y * doi.y;
			
			let tgtSq = this.data.far * this.data.far - vSq;
			if(hSq < tgtSq)
			{
				tgtSq = this.data.near * this.data.near - vSq;
				if(hSq > tgtSq)
				{
					tgtSq = offX * offX;
					if(hSq > tgtSq)
					{
						tgtSq = -1;
					}
				}
			}

			if(tgtSq > 0)
			{
				const u = thisMat[0] * doi.x + thisMat[2] * doi.z;
				const uSq = u * u;
				const dz = ((tgtSq > uSq) ? Math.sqrt( tgtSq - uSq ) : 0) - ((hSq > uSq) ? Math.sqrt( hSq - uSq ) : 0);
				doi.x -= thisMat[2] * dz;
				doi.z += thisMat[0] * dz;
				this.el.object3D.position.set(poi.x + doi.x, poi.y + doi.y, poi.z + doi.z );
			}
			else if(zoom != 0)
				this.el.object3D.position.set(poi.x + doi.x, poi.y + doi.y, poi.z + doi.z );

			const h = Math.sqrt( doi.x * doi.x + doi.z * doi.z );
			this.el.object3D.rotation.y = Math.atan2( doi.x, doi.z ) - 2.0 * Math.asin( 0.5 * offX / h);
			this.el.object3D.rotation.x = clamp( Math.atan2( -doi.y, h ), -0.35 * Math.PI, 0.35 * Math.PI );
		}
		else // RE4 Camera
		{
			//Distance including zoom :
			doi.x -= offX * thisMat[0];
			doi.y -= offX * thisMat[1];
			doi.z -= offX * thisMat[2];
			let distance = Math.sqrt(doi.x*doi.x + doi.y*doi.y + doi.z*doi.z);
			
			//Clamp to [near,far]
			if(distance < this.data.near) distance = this.data.near;
			else if(distance > this.data.far) distance = this.data.far;
			
			
			this.el.object3D.position.set(
				poi.x + offX * thisMat[0] + distance * thisMat[8],
				poi.y + offX * thisMat[1] + distance * thisMat[9],
				poi.z + offX * thisMat[2] + distance * thisMat[10] );
		}
	}
});